import { useCallback, useEffect, useState } from "react";
import { Check, Lock, Unlock, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const pretty = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** 30-minute emailed-OTP unlock for editing dropdown values, plus the staff request queue. */
export const DropdownLockCard = ({ onUnlockChange, onApproved }) => {
  const { user, loadOptions } = useAuth();
  const [state, setState] = useState({ can_edit: false, unlocked: false, minutes: 30 });
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [requests, setRequests] = useState([]);
  const isAdmin = user?.role === "admin";

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/options/security/unlock");
      setState(data);
      onUnlockChange?.(data);
    } catch (e) { console.warn("dropdown lock status unavailable:", e); }
    try {
      const { data } = await api.get("/option-requests", { params: { status: "pending" } });
      setRequests(data);
    } catch (e) { console.warn("option requests unavailable:", e); }
  }, [onUnlockChange]);

  useEffect(() => { refresh(); const t = setInterval(refresh, 30000); return () => clearInterval(t); },
    [refresh]);

  const request = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/options/security/otp/request");
      setSent(true);
      toast.success(`Code sent to ${data.sent_to}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/options/security/otp/verify", { code: code.trim() });
      setCode(""); setSent(false);
      toast.success(`Dropdown editing unlocked for ${data.minutes} minutes`);
      await refresh();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const lock = async () => {
    try { await api.post("/options/security/lock"); await refresh(); toast.success("Locked again"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const decide = async (req, approve) => {
    try {
      await api.post(`/option-requests/${req.request_id}/${approve ? "approve" : "reject"}`, { note: "" });
      toast.success(approve ? `"${req.value}" added to ${pretty(req.category)}` : "Request rejected");
      await loadOptions();
      await refresh();
      onApproved?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const until = state.expires_at ? new Date(state.expires_at).toLocaleTimeString() : null;

  if (!isAdmin) {
    return (
      <div className="rounded-md border border-[#E5E7EB] bg-white p-5" data-testid="dropdown-lock-card">
        <div className="flex items-start gap-3">
          <Lock className="h-5 w-5 text-[#6B7280] mt-0.5" />
          <div>
            <h2 className="font-heading font-bold text-[#111827]">Dropdown values are managed by admins</h2>
            <p className="text-xs text-[#6B7280] mt-0.5">
              You can request a new value from any dropdown while filling a form — an administrator
              approves it and it becomes available to everyone.
            </p>
          </div>
        </div>
        {requests.length > 0 && (
          <div className="mt-3 space-y-1" data-testid="my-option-requests">
            {requests.map((q) => (
              <p key={q.request_id} className="text-xs text-[#6B7280]">
                <strong className="text-[#111827]">{q.value}</strong> in {pretty(q.category)} — awaiting approval
              </p>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white p-5" data-testid="dropdown-lock-card">
      <div className="flex items-start gap-3">
        {state.unlocked ? <Unlock className="h-5 w-5 text-[#2E7D32] mt-0.5" />
          : <Lock className="h-5 w-5 text-[#C92A2A] mt-0.5" />}
        <div>
          <h2 className="font-heading font-bold text-[#111827]">Dropdown settings lock</h2>
          <p className="text-xs text-[#6B7280] mt-0.5">
            Only administrators can change dropdown values, and only after confirming a code emailed to
            you. One code unlocks editing for {state.minutes} minutes.
          </p>
        </div>
      </div>

      <div className="mt-4">
        {state.unlocked ? (
          <div className="flex flex-wrap items-center gap-3" data-testid="dropdown-unlocked">
            <span className="text-xs text-[#2E7D32] font-medium">
              Editing unlocked{until ? ` until ${until}` : ""}
            </span>
            <Button variant="outline" size="sm" onClick={lock} data-testid="dropdown-lock-btn">Lock now</Button>
          </div>
        ) : (
          <form onSubmit={verify} className="flex flex-wrap items-end gap-2" data-testid="dropdown-locked">
            <Button type="button" onClick={request} disabled={busy} data-testid="dropdown-otp-request-btn"
              className="bg-[#002FA7] hover:bg-[#00237C] text-white">
              {sent ? "Resend code" : "Email me a code"}
            </Button>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code"
              className="w-40" data-testid="dropdown-otp-code-input" />
            <Button type="submit" variant="outline" disabled={busy || code.trim().length < 4}
              data-testid="dropdown-otp-verify-btn">Unlock editing</Button>
          </form>
        )}
      </div>

      <div className="mt-5 border-t border-[#E5E7EB] pt-4">
        <h3 className="font-heading font-semibold text-sm text-[#111827]">
          Requests from staff {requests.length > 0 && `(${requests.length})`}
        </h3>
        {requests.length === 0 ? (
          <p className="text-xs text-[#6B7280] mt-1" data-testid="option-requests-empty">
            No pending requests.
          </p>
        ) : (
          <>
            {!state.unlocked && (
              <p className="text-xs text-[#B58900] mt-1" data-testid="requests-locked-hint">
                Unlock dropdown editing above to approve or reject these.
              </p>
            )}
          <div className="mt-2 space-y-2" data-testid="option-requests-list">
            {requests.map((q) => (
              <div key={q.request_id} className="flex flex-wrap items-center gap-2"
                data-testid={`option-request-${q.request_id}`}>
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm text-[#111827]">
                    <strong>{q.value}</strong> <span className="text-[#6B7280]">in {pretty(q.category)}</span>
                  </p>
                  <p className="text-xs text-[#6B7280]">
                    asked by {q.requested_by}{q.reason ? ` — ${q.reason}` : ""}
                  </p>
                </div>
                <Button size="sm" onClick={() => decide(q, true)} data-testid={`approve-request-${q.request_id}`}
                  disabled={!state.unlocked} title={state.unlocked ? "" : "Unlock dropdown editing first"}
                  className="bg-[#2E7D32] hover:bg-[#1f5c24] text-white h-8">
                  <Check className="h-3.5 w-3.5 mr-1" /> Approve
                </Button>
                <Button size="sm" variant="outline" onClick={() => decide(q, false)} className="h-8"
                  disabled={!state.unlocked} title={state.unlocked ? "" : "Unlock dropdown editing first"}
                  data-testid={`reject-request-${q.request_id}`}>
                  <X className="h-3.5 w-3.5 mr-1" /> Reject
                </Button>
              </div>
            ))}
          </div>
          </>
        )}
      </div>
    </div>
  );
};
